import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import api from "@/lib/api";
import DashboardShell from "@/components/DashboardShell";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Bookmark, Copy, Trash2, Loader2, Wand2, Hash, UserCircle, CalendarPlus, Flame } from "lucide-react";

const ICONS = { script: Wand2, caption: Hash, bio: UserCircle, idea: Flame, hashtags: Hash };

const flatten = (item) => {
  const c = item.content || {};
  if (item.type === "script") return `${c.title || ""}\n\nHook: ${c.hook || ""}\n\n${(c.beats || []).join("\n")}\n\nCTA: ${c.cta || ""}\n\n${c.caption || ""}\n${(c.hashtags || []).map((h) => "#" + String(h).replace(/^#/, "")).join(" ")}`;
  if (item.type === "caption") return `${(c.captions || []).join("\n\n")}\n\n${(c.hashtags || []).map((h) => "#" + String(h).replace(/^#/, "")).join(" ")}`;
  if (item.type === "bio") return (c.bios || []).join("\n");
  if (item.type === "idea") return `${c.title || ""}\n\nHook: ${c.hook || ""}\nFormat: ${c.format || ""}\nWhy it works: ${c.why_viral || ""}\n\n${(c.hashtags || []).map((h) => "#" + String(h).replace(/^#/, "")).join(" ")}`;
  if (item.type === "hashtags") {
    const tags = (arr) => (arr || []).map((h) => "#" + String(h).replace(/^#/, "")).join(" ");
    return `Topic: ${c.topic || ""}\n\nBroad reach:\n${tags(c.broad)}\n\nNiche:\n${tags(c.niche)}\n\nTrending:\n${tags(c.trending)}\n\nKeywords: ${(c.keywords || []).join(", ")}${(c.tips || []).length ? "\n\nTips:\n" + c.tips.join("\n") : ""}`;
  }
  return JSON.stringify(c);
};

const Library = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sched, setSched] = useState(null); // item being scheduled
  const [when, setWhen] = useState("");

  const load = () => api.get("/library").then((r) => setItems(r.data.items)).catch(() => toast.error("Could not load library")).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const remove = async (id) => {
    setItems((x) => x.filter((i) => i.id !== id));
    try { await api.delete(`/library/${id}`); toast.success("Removed"); } catch { toast.error("Could not remove"); load(); }
  };

  const scheduleItem = async () => {
    if (!when) { toast.error("Pick a date & time"); return; }
    try {
      await api.post("/calendar", { platform: sched.platform || "tiktok", title: sched.title, notes: flatten(sched), scheduled_at: new Date(when).toISOString() });
      toast.success("Added to your calendar!");
      setSched(null); setWhen("");
    } catch { toast.error("Could not schedule"); }
  };

  return (
    <DashboardShell>
      <div className="p-6 md:p-10 max-w-4xl mx-auto">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 rounded-xl bg-primary/15 text-primary flex items-center justify-center"><Bookmark className="w-5 h-5" /></div>
          <h1 className="font-heading text-3xl font-black tracking-tight">Idea Library</h1>
        </div>
        <p className="text-muted-foreground mb-8">Everything you've saved from the AI Studio and Viral Lab, ready to reuse.</p>

        {loading ? (
          <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 text-primary animate-spin" /></div>
        ) : items.length === 0 ? (
          <div className="rounded-2xl border border-border/50 bg-card p-12 text-center text-muted-foreground" data-testid="library-empty">
            Nothing saved yet — hit "Save" on any AI Studio or Viral Lab result to build your library.
          </div>
        ) : (
          <div className="space-y-3" data-testid="library-list">
            {items.map((it, i) => {
              const Icon = ICONS[it.type] || Bookmark;
              return (
                <motion.div key={it.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: Math.min(i * 0.04, 0.3) }}
                  className="rounded-xl border border-border/50 bg-card p-5" data-testid={`library-item-${it.id}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex items-start gap-3 min-w-0">
                      <div className="w-9 h-9 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center shrink-0"><Icon className="w-4 h-4 text-primary" /></div>
                      <div className="min-w-0">
                        <div className="font-semibold truncate">{it.title}</div>
                        <div className="flex items-center gap-2 mt-1">
                          <Badge className="bg-primary/10 text-primary border border-primary/20 capitalize text-[10px]">{it.type}</Badge>
                          {it.platform && <span className="text-xs text-muted-foreground capitalize">{it.platform}</span>}
                        </div>
                      </div>
                    </div>
                    <div className="flex gap-1 shrink-0">
                      <Button variant="ghost" size="sm" onClick={() => { setSched(it); setWhen(""); }} title="Add to calendar" data-testid={`library-schedule-${it.id}`}><CalendarPlus className="w-4 h-4 text-primary" /></Button>
                      <Button variant="ghost" size="sm" onClick={() => { navigator.clipboard.writeText(flatten(it)); toast.success("Copied!"); }} data-testid={`library-copy-${it.id}`}><Copy className="w-4 h-4" /></Button>
                      <Button variant="ghost" size="sm" onClick={() => remove(it.id)} className="text-secondary hover:text-secondary" data-testid={`library-delete-${it.id}`}><Trash2 className="w-4 h-4" /></Button>
                    </div>
                  </div>
                  <pre className="mt-3 text-xs text-muted-foreground whitespace-pre-wrap font-sans line-clamp-4">{flatten(it)}</pre>
                </motion.div>
              );
            })}
          </div>
        )}

        <Dialog open={!!sched} onOpenChange={(o) => !o && setSched(null)}>
          <DialogContent className="bg-card border-border">
            <DialogHeader><DialogTitle className="font-heading">Add to calendar</DialogTitle>
              <DialogDescription>Pick when to publish this saved idea.</DialogDescription>
            </DialogHeader>
            <div className="py-2 space-y-3">
              <div className="text-sm"><span className="text-muted-foreground">Post:</span> <b>{sched?.title}</b></div>
              <div>
                <Label className="text-sm">When to publish</Label>
                <Input type="datetime-local" value={when} onChange={(e) => setWhen(e.target.value)}
                  className="mt-1.5 h-11 bg-transparent border-white/10" data-testid="library-schedule-datetime" />
              </div>
            </div>
            <DialogFooter>
              <Button onClick={scheduleItem} className="rounded-full bg-primary text-background font-bold" data-testid="library-schedule-save">Schedule it</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardShell>
  );
};

export default Library;
